import { ISendChatMessageParams, ISendChatMessageResponse } from "../../interfaces/api/nest/sendInAppChatMessage"
import ApiUtils from "../ApiUtils"
import { INestService } from "./INestService"
import { IFetchDdsScoresParams, IFetchDdsScoresResponse } from "../../interfaces/api/nest/fetchDdsScores"
import { IFetchCoachCommunityParams, IFetchCoachCommunityResponse } from "../../interfaces/api/nest/filterCoachCoachCommunities"
import { ICommunity, ICommunityResponse, ICommunitySearchParam, IFilterCommunitiesReqParam, IFilterCommunitiesResponse } from "../../interfaces/api/nest/filterCommunities"
import { IFilterPollResponse, IPollDetails, IPollFilterParams, IUserSegment } from "../../interfaces/api/nest/polls"
import { PollUserType } from "../../components/polls/helper"
import {
	IChallengeDetails,
	IChallengeLeaderboardResponse,
	IChallengesFilterParams,
	IChallengeSubLeaderboardResponse,
	IChallengeUserFilterResponses,
	IChallengeUserResponseFilterParam,
	IFilterChallengeResponse,
	IUserResponse,
} from "../../interfaces/api/nest/challenges"
import { IOptionType } from "../../containers/challenges/helper"
import { ICheckUserVoipEligibilityResponse } from "../../interfaces/api/nest/checkUserVoipEligibility"
import { IInitiateVoipParams, IInitiateVoipResponse } from "../../interfaces/api/nest/initiateVoip"
import { IGetVoipJoinTokenParams, IGetVoipJoinTokenResponse } from "../../interfaces/api/nest/getVoipJoinToken"
import { IUpdateVoipCallStatusParams, IUpdateVoipCallStatusResponse } from "../../interfaces/api/nest/updateVoipStatus"
import { IFetchVoipCallStatusParams, IFetchVoipCallStatusResponse } from "../../interfaces/api/nest/fetchVoipCallStatus"
import { IEndVoipCallParams, IEndVoipCallResponse } from "../../interfaces/api/nest/endVoipCall"
import { ICreateChatTemplatePayload, ICreateChatTemplateResponse } from "../../interfaces/api/nest/createChatTemplate"
import { IUpdateChatTemplatePayload, IUpdateChatTemplateResponse } from "../../interfaces/api/nest/updateChatTemplate"
import { IFetchAllChatTemplatesPayload, IFetchAllChatTemplatesResponse } from "../../interfaces/api/nest/fetchAllChatTemplates"
import { IFilterChallengesUserResponsePayload, IFilterChallengesUserResponseRes } from "../../interfaces/api/nest/filterChallengesUserResponse"
import { IFetchUploadUrlForChatAssetPayload } from "../../interfaces/api/nest/fetchUploadUrlForChatAsset"

export const NEST_SERVICE_API_PREFIX = "/nest"

const NestUrls = {
	filterCoachCommunities: "/coachCommunity/filter",
	fetchAdmins: "/communityUsers/admins",
	filterCommunities: "/communityUsers/filter",
	fetchDdsScores: (params: IFetchDdsScoresParams): string => "/pollsupport/poll/dds/response/all/?" + ApiUtils.requestToQueryParams(params),
	sendMessage: "/message/send",
	filterPolls: "/pollsupport/poll/fetchAll",
	pollDetails: (pollId: number) => `/pollsupport/poll/fetchDetailed?pollId=${pollId}`,
	createPoll: "/pollsupport/poll/",
	savePollInDraft: "/pollsupport/poll/saveDraft",
	updatePollInDraft: "/pollsupport/poll/updateDraft",
	publishDraftPoll: "/pollsupport/poll/publishDraft",
	deletePoll: (pollId: number, userId: number, userType: string) => `/pollsupport/poll/?pollId=${pollId}&userId=${userId}&userType=${userType}`,
	uploadSignedUrl: (count: number) => `/pollsupport/poll/signedUploadURL?count=${count}`,
	uploadSignedGetUrl: (filename: string) => `/pollsupport/poll/signedGetURL??filename=${filename}`,
	getFreemiumUserSegment: "/pollsupport/poll/freemiumUsersSegment",
	getPremiumUserSegment: "/pollsupport/poll/premiumUsersSegment",
	getPremiumUserSegmentForChallenges: "/challenges/premiumUsersSegment",
	filterChallenges: "/challenges/fetchAll",
	challengeDetails: (challengeId: number) => `/challenges/challengeDetailed?challengeId=${challengeId}`,
	createChallenge: "/challenges/",
	updateDraftChallenge: "/challenges/updateDraft",
	publishDraftChallenge: "/challenges/publishDraft",
	deleteChallenge: (challengeId: number, userId: number, userType: string) => `/challenges/?challengeId=${challengeId}&userId=${userId}&userType=${userType}`,
	uploadChallengeAssetSignedUrl: (count: number) => `/challenges/signedUploadURL?count=${count}`,
	uploadChallengeAssetSignedGetUrl: (filename: string) => `/challenges/signedGetURL?filename=${filename}`,
	challengeTypes: "/challenges/challengeTypes",
	challengeConfig: (challengeType: string) => `/challengesConfig/config?challengeType=${challengeType}`,
	filterChallengeUserResponse: "/challengeUserResponse/allChallengeUserResponses?",
	fetchChallengeLeaderboard: "/leaderboard/leaderboardForChallenge?",
	fetchChallengeSubLeaderboard: "/leaderboard/coach/subleaderboard?",
	rejectUserResponse: "/challengeUserResponse/reject",
	blockUserForChallenge: "/challenges/blockUser",
	fetchAllUserResponses: (challengeId: number, userId: number) => `/challengeUserResponse/allResponsesForUser?challengeId=${challengeId}&userId=${userId}`,
	searchCommunities: "/community/search",
	fetchCommunityById: (communityId: number) => `/community/${communityId}`,
	fetchPollScenarios: (pollType: string) => `/pollsupport/poll/config/configScenarios?pollType=${pollType}`,
	publishSurvey: (configName: string) => `/pollsupport/poll/publish/config?configName=${configName}`,
	checkUserVoipEligibilty: "/voip/user/eligibility?",
	initiateVoip: "/voip/initiate",
	getVoipJoinToken: "/voip/join/token?",
	voipStatus: "/voip/status",
	endVoipCall: "/voip/end",
	downloadVoipRecording: "/voip/recording/download?",
	messageTemplate: "/messageTemplate/",
	fetchMessageTemplates: "/messageTemplate/search",
	fetchAssetDownloadPath: (fileKey: string) => `/message/media/downloadPath?fileKey=${fileKey}`,
	challengeUserResponseFilter: "/challengeUserResponse/filter",
	fetchUploadUrlForChatAsset: "/message/media/uploadPath"
}

export default class NestServiceApis implements INestService {
	getServiceUrl = (url: string) => NEST_SERVICE_API_PREFIX + url

	sendInAppChatMessage = (params: ISendChatMessageParams): Promise<ISendChatMessageResponse> => {
		const url = this.getServiceUrl(NestUrls.sendMessage)
		return ApiUtils.post(url, params)
	}

	filterCoachCommunities = (params: IFetchCoachCommunityParams): Promise<IFetchCoachCommunityResponse> => {
		const url = this.getServiceUrl(NestUrls.filterCoachCommunities)
		return ApiUtils.post(url, params)
	}

	filterPolls = (params: IPollFilterParams): Promise<IFilterPollResponse> => {
		const url = `${this.getServiceUrl(NestUrls.filterPolls)}?${ApiUtils.requestToQueryParams(params)}`
		return ApiUtils.get(url)
	}

	fetchPollDetails = (pollId: number): Promise<IPollDetails> => {
		const url = this.getServiceUrl(NestUrls.pollDetails(pollId))
		return ApiUtils.get(url)
	}

	createPoll = (poll: IPollDetails): Promise<IPollDetails> => {
		return ApiUtils.post(this.getServiceUrl(NestUrls.createPoll), poll)
	}

	savePollInDraft = (poll: IPollDetails): Promise<IPollDetails> => {
		return ApiUtils.post(this.getServiceUrl(NestUrls.savePollInDraft), poll)
	}

	updatePollInDraft = (poll: IPollDetails): Promise<IPollDetails> => {
		return ApiUtils.post(this.getServiceUrl(NestUrls.updatePollInDraft), poll)
	}

	publishDraftPoll = (poll: IPollDetails): Promise<IPollDetails> => {
		return ApiUtils.post(this.getServiceUrl(NestUrls.publishDraftPoll), poll)
	}

	deletePoll = (pollId: number, userId: number, userType: PollUserType): Promise<void> => {
		const url = this.getServiceUrl(NestUrls.deletePoll(pollId, userId, userType))
		return ApiUtils.delete(url, {})
	}

	getUploadSignedUrl = (count = 1): Promise<Record<string, string>> => {
		return ApiUtils.get(this.getServiceUrl(NestUrls.uploadSignedUrl(count)))
	}

	getSignedUrl = (filename: string): Promise<Record<string, string>> => {
		return ApiUtils.get(this.getServiceUrl(NestUrls.uploadSignedGetUrl(filename)))
	}

	getFreemiumUserSegment = (): Promise<IUserSegment> => {
		return ApiUtils.get(this.getServiceUrl(NestUrls.getFreemiumUserSegment))
	}

	getPremiumUserSegment = (): Promise<IUserSegment> => {
		return ApiUtils.get(this.getServiceUrl(NestUrls.getPremiumUserSegment))
	}

	getPremiumUserSegmentForChallenges = (): Promise<IUserSegment> => {
		return ApiUtils.get(this.getServiceUrl(NestUrls.getPremiumUserSegmentForChallenges))
	}
	
	filterCommunities = (params: IFilterCommunitiesReqParam): Promise<IFilterCommunitiesResponse> => {
		const url = this.getServiceUrl(NestUrls.filterCommunities)
		return ApiUtils.post(url, params)
	}

	fetchAdminIds = (): Promise<number[]> => {
		const url = this.getServiceUrl(NestUrls.fetchAdmins)
		return ApiUtils.get(url)
	}
	/**
	 * challenges apis
	 */
	filterChallenges = (params: IChallengesFilterParams): Promise<IFilterChallengeResponse> => {
		const url = `${this.getServiceUrl(NestUrls.filterChallenges)}?${ApiUtils.requestToQueryParams(params)}`
		return ApiUtils.get(url)
	}
	fetchChallengeDetails = (challengeId: number): Promise<IChallengeDetails> => {
		const url = this.getServiceUrl(NestUrls.challengeDetails(challengeId))
		return ApiUtils.get(url)
	}
	createChallenge = (challenge: IChallengeDetails): Promise<IChallengeDetails> => {
		return ApiUtils.post(this.getServiceUrl(NestUrls.createChallenge), challenge)
	}
	updateChallengeInDraft = (challenge: IChallengeDetails): Promise<IChallengeDetails> => {
		return ApiUtils.post(this.getServiceUrl(NestUrls.updateDraftChallenge), challenge)
	}
	publishDraftChallenge = (challenge: IChallengeDetails): Promise<IChallengeDetails> => {
		return ApiUtils.post(this.getServiceUrl(NestUrls.publishDraftChallenge), challenge)
	}
	deleteChallenge = (challengeId: number, userId: number, userType: PollUserType): Promise<void> => {
		const url = this.getServiceUrl(NestUrls.deleteChallenge(challengeId, userId, userType))
		return ApiUtils.delete(url, {})
	}
	getChallengeAssetUploadSignedUrl = (count = 1): Promise<Record<string, string>> => {
		return ApiUtils.get(this.getServiceUrl(NestUrls.uploadChallengeAssetSignedUrl(count)))
	}
	getChallengeAssetSignedUrl = (filename: string): Promise<Record<string, string>> => {
		return ApiUtils.get(this.getServiceUrl(NestUrls.uploadChallengeAssetSignedGetUrl(filename)))
	}
	getChallengeTypes = (): Promise<IOptionType[]> => {
		return ApiUtils.get(this.getServiceUrl(NestUrls.challengeTypes))
	}
	getChallengeConfig = (challengeType: string): Promise<Partial<IChallengeDetails>> => {
		return ApiUtils.get(this.getServiceUrl(NestUrls.challengeConfig(challengeType)))
	}

	getChallengeUserResponse = (params: IChallengeUserResponseFilterParam): Promise<IChallengeUserFilterResponses> => {
		const url = `${this.getServiceUrl(NestUrls.filterChallengeUserResponse)}${ApiUtils.requestToQueryParams(params)}`
		return ApiUtils.get(url)
	}

	fetchChallengeLeaderboard = (params: IChallengeUserResponseFilterParam): Promise<IChallengeLeaderboardResponse> => {
		const url = `${this.getServiceUrl(NestUrls.fetchChallengeLeaderboard)}${ApiUtils.requestToQueryParams(params)}`
		return ApiUtils.get(url)
	}

	fetchChallengeSubLeaderboard = (params: IChallengeUserResponseFilterParam): Promise<IChallengeSubLeaderboardResponse> => {
		const url = `${this.getServiceUrl(NestUrls.fetchChallengeSubLeaderboard)}${ApiUtils.requestToQueryParams(params)}`
		return ApiUtils.get(url)
	}

	rejectUserResponse = (userId: number, userResponseId: number): Promise<void> => {
		const url = ApiUtils.requestToQueryParams({ userId, userResponseId })
		return ApiUtils.put(`${this.getServiceUrl(NestUrls.rejectUserResponse)}?${url}`, {})
	}

	blockUserForChallenge = (userId: number): Promise<void> => {
		return ApiUtils.post(this.getServiceUrl(NestUrls.blockUserForChallenge), { userId })
	}
	fetchAllUserResponses = (userId: number, challengeId: number): Promise<IUserResponse[]> => {
		const url = this.getServiceUrl(NestUrls.fetchAllUserResponses(challengeId, userId))
		return ApiUtils.get(url)
	}
	searchCommunities = (param: ICommunitySearchParam): Promise<ICommunityResponse> => {
		const url = `${this.getServiceUrl(NestUrls.searchCommunities)}?${ApiUtils.requestToQueryParams(param)}`
		return ApiUtils.get(url)
	}
	fetchCommunityById = (communityId: number): Promise<ICommunity> => {
		return ApiUtils.get(this.getServiceUrl(NestUrls.fetchCommunityById(communityId)))
	}
	fetchPollScenarios = (pollType: string): Promise<string[]> => {
		return ApiUtils.get(this.getServiceUrl(NestUrls.fetchPollScenarios(pollType)))
	}
	publishSurvey = (configName: string): Promise<void> => {
		return ApiUtils.post(this.getServiceUrl(NestUrls.publishSurvey(configName)), {})
	}

	fetchDdsScores = (params: IFetchDdsScoresParams): Promise<IFetchDdsScoresResponse> => {
		const url = this.getServiceUrl(NestUrls.fetchDdsScores(params))
		return ApiUtils.get(url)
	}

	checkUserVoipElgibility = (userId: string): Promise<ICheckUserVoipEligibilityResponse> => {
		const url = this.getServiceUrl(NestUrls.checkUserVoipEligibilty + ApiUtils.requestToQueryParams({ userId }))
		return ApiUtils.get(url)
	}

	initiateVoip = (params: IInitiateVoipParams): Promise<IInitiateVoipResponse> => {
		const url = this.getServiceUrl(NestUrls.initiateVoip)
		return ApiUtils.post(url, params)
	}

	getVoipJoinToken = (params: IGetVoipJoinTokenParams): Promise<IGetVoipJoinTokenResponse> => {
		const url = this.getServiceUrl(NestUrls.getVoipJoinToken + ApiUtils.requestToQueryParams(params))
		return ApiUtils.get(url)
	}

	updateVoipCallStatus = (params: IUpdateVoipCallStatusParams): Promise<IUpdateVoipCallStatusResponse> => {
		const url = this.getServiceUrl(NestUrls.voipStatus)
		return ApiUtils.post(url, params)
	}

	fetchVoipCallStatus = (params: IFetchVoipCallStatusParams): Promise<IFetchVoipCallStatusResponse> => {
		const url = this.getServiceUrl(NestUrls.voipStatus + "?" + ApiUtils.requestToQueryParams(params))
		return ApiUtils.get(url)
	}

	endVoipCall = (params: IEndVoipCallParams): Promise<IEndVoipCallResponse> => {
		const url = this.getServiceUrl(NestUrls.endVoipCall)
		return ApiUtils.post(url, params)
	}

	downloadVoipRecording = (voipInstanceId: number): Promise<any> => {
		const url = this.getServiceUrl(NestUrls.downloadVoipRecording + ApiUtils.requestToQueryParams({ voipInstanceId }))
		return ApiUtils.get(url)
	}

	createChatTemplate = (payload: ICreateChatTemplatePayload): Promise<ICreateChatTemplateResponse> => {
		const url = this.getServiceUrl(NestUrls.messageTemplate)
		return ApiUtils.post(url, payload)
	}

	updateChatTempplate = (payload: IUpdateChatTemplatePayload): Promise<IUpdateChatTemplateResponse> => {
		const url = this.getServiceUrl(NestUrls.messageTemplate + payload.id)
		delete payload["id"]
		return ApiUtils.patch(url, payload)
	}

	deleteChatTemplate = (payload: IUpdateChatTemplatePayload): Promise<IUpdateChatTemplateResponse> => {
		const url = this.getServiceUrl(NestUrls.messageTemplate + payload.id)
		delete payload["id"]
		return ApiUtils.patch(url, payload)
	}
	fetchAllChatTemplates = (payload: IFetchAllChatTemplatesPayload): Promise<IFetchAllChatTemplatesResponse> => {
		const url = this.getServiceUrl(NestUrls.fetchMessageTemplates)
		return ApiUtils.post(url, payload)
	}

	fetchAssetDownloadPath = (fileKey: string): Promise<any> => {
		const url = this.getServiceUrl(NestUrls.fetchAssetDownloadPath(fileKey))
		return ApiUtils.get(url)
	}

	filterChallengesUserResponse = (payload: IFilterChallengesUserResponsePayload): Promise<IFilterChallengesUserResponseRes> => {
		const url = this.getServiceUrl(NestUrls.challengeUserResponseFilter)
		return ApiUtils.post(url, payload)
	}

	fetchUploadUrlForChatAsset = (payload: IFetchUploadUrlForChatAssetPayload): Promise<Record<string, string>> => {
		const url = this.getServiceUrl(NestUrls.fetchUploadUrlForChatAsset) + "?" + ApiUtils.requestToQueryParams(payload)
		return ApiUtils.get(url)
	}
}
